import React, { useState, useEffect } from "react";
import { firestore, auth } from "../firebaseConfig";
import { collection, getDocs, query, where,doc,getDoc } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { analytics } from "../firebaseConfig";
import { logEvent } from "firebase/analytics";

const ContractList = () => {
  const [user] = useAuthState(auth);
  const [contracts, setContracts] = useState([]);
  const [orgId, setOrgId] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "createdAt", direction: "ascending" });

  useEffect(() => {
    logEvent(analytics, 'contract_list_page');
    if (user) {
      const fetchUser = async () => {
        const userQuery = doc(firestore, "users", user.uid)
        const userSnapshot = await getDoc(userQuery);
        if (userSnapshot.exists()) {
          const userData = userSnapshot.data();
          setOrgId(userData.orgId);
          setRole(userData.role);
        }
      };
      fetchUser();
    }
  }, [user]);

  useEffect(() => {
    if (orgId) {
      const fetchContracts = async () => {
        setLoading(true);
        const contractQuery = query(collection(firestore, "contracts"), where("orgId", "==", orgId));
        const contractSnapshot = await getDocs(contractQuery);
        const contractsData = contractSnapshot.docs.map(doc => {
          const data = doc.data();
          const flattenedSummary = data.summary || {};
          return {
            id: doc.id,
            fileName: data.fileName,
            fileUrl: data.fileUrl,
            uploadedBy: data.uploadedBy,
            createdAt: data.createdAt ? data.createdAt.toDate().toLocaleString() : null,
            effectiveDate: flattenedSummary.effectiveDate || "",
            paymentAmount: flattenedSummary.paymentAmount || "",
            paymentTerms: flattenedSummary.paymentTerms || "",
            renewalTerms: flattenedSummary.renewalTerms || "",
          };
        });
        setContracts(contractsData);
        setLoading(false);
      };
      fetchContracts();
    }
  }, [orgId]);

  // Handle search
  const filteredContracts = contracts.filter(contract =>
    contract.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contract.createdAt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contract.effectiveDate.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contract.paymentAmount.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contract.paymentTerms.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contract.renewalTerms.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle sorting
  const sortedContracts = [...filteredContracts].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === "ascending" ? -1 : 1;
    if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === "ascending" ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === "ascending" ? "descending" : "ascending",
    }));
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(contracts);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Contracts");
    XLSX.writeFile(workbook, "contracts_data.xlsx");
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white dark:bg-gray-900 text-gray-200 min-h-screen">
      <h2 className="text-3xl font-bold mb-6 text-center text-blue-600">Contracts</h2>

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
        {/* Search Input */}
        <div className="w-full lg:w-auto flex-1">
          <input
            type="text"
            placeholder="Search contracts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-3 rounded-lg dark:bg-gray-800 dark:text-gray-200 text-black border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-600"
          />
        </div>

        {/* Controls (Manage Members, Sort, Export) */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* {role === "admin" && (
            <Link to="/manage-members" className="text-blue-400 hover:underline font-medium text-center lg:text-left">
              Manage Members
            </Link>
          )} */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <select
              value={sortConfig.key}
              onChange={(e) => handleSort(e.target.value)}
              className="p-3 rounded-lg dark:bg-gray-800 dark:text-gray-200 text-black border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              <option value="createdAt">Sort by Created At</option>
              <option value="effectiveDate">Sort by Effective Date</option>
              <option value="paymentAmount">Sort by Payment Amount</option>
            </select>
            <button
              onClick={exportToExcel}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Export to Excel
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-blue-400 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
        </div>
      ) : (
        <div className="space-y-6">
          {sortedContracts.length > 0 ? (
            sortedContracts.map((contract) => (
              <div key={contract.id} className="dark:bg-gray-800 p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                <h3 className="text-lg font-semibold dark:text-blue-400 text-black mb-2 truncate" title={contract.fileName}>
                  {contract.fileName}
                </h3>
                <div className="space-y-2 text-sm text-black dark:text-gray-200">
                  <p><strong>Created At:</strong> {contract.createdAt}</p>
                  <p><strong>Effective Date:</strong> {contract.effectiveDate}</p>
                  <p><strong>Payment Amount:</strong> {contract.paymentAmount}</p>
                  <p><strong>Payment Terms:</strong> {contract.paymentTerms}</p>
                  <p><strong>Renewal Terms:</strong> {contract.renewalTerms}</p>
                </div>
                <div className="mt-4 flex space-x-3">
                  <a
                    href={contract.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1 text-sm dark:text-blue-400 text-black border border-blue-400 rounded hover:bg-blue-900 transition-colors"
                  >
                    View File
                  </a>
                  <Link
                    to={`/contracts/${contract.id}`}
                    className="px-3 py-1 text-sm dark:text-blue-400 text-black border border-blue-400 rounded hover:bg-blue-900 transition-colors"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-400">No contracts found.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ContractList;